import React, {
  useEffect,
} from "react";

import PropTypes from "prop-types";

/* =========================================================
   GROUP TYPE LABELS
========================================================= */

const GROUP_TYPE_LABELS = {
  passage: "Passage",
  clozeTest: "Cloze Test",
  commonDirections: "Common Directions",
  caselet: "Caselet",
  paraJumble: "Para Jumble",
  sentenceBased: "Sentence Based",
  other: "Other",
};

/* =========================================================
   HELPERS
========================================================= */

function getId(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return (
      value?._id ||
      value?.id ||
      value?.chapterId ||
      ""
    );
  }

  return String(value);
}

function getChapterName(chapter) {
  if (!chapter) {
    return "All Chapters";
  }

  if (typeof chapter === "string") {
    return chapter;
  }

  return (
    chapter?.name ||
    chapter?.title ||
    chapter?.chapterName ||
    getId(chapter) ||
    "All Chapters"
  );
}

function getGroupTypeLabel(groupType) {
  return (
    GROUP_TYPE_LABELS[groupType] ||
    groupType ||
    "Not specified"
  );
}

/* =========================================================
   EMPTY STATE
========================================================= */

function EmptyState({
  onAddSection,
  disabled = false,
}) {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
      <p className="text-sm font-semibold text-gray-700">
        No blueprint sections added
      </p>

      <p className="mt-1 text-xs text-gray-500">
        Add at least one section to define how
        question groups should be selected.
      </p>

      {onAddSection && (
        <button
          type="button"
          onClick={onAddSection}
          disabled={disabled}
          className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Add Section
        </button>
      )}
    </div>
  );
}

/* =========================================================
   SECTION CARD
========================================================= */

function BlueprintSectionCard({
  section,
  index,
  onEdit,
  onRemove,
  canRemove,
  disabled,
}) {
  const sectionName =
    section?.name?.trim() ||
    `Section ${index + 1}`;

  const chapterName = getChapterName(
    section?.chapter
  );

  const groupTypeLabel =
    getGroupTypeLabel(
      section?.groupType
    );

  const selectionCount = Number(
    section?.selectionCount ?? 0
  );

  const marksPerQuestion = Number(
    section?.marksPerQuestion ?? 0
  );

  const negativeMarks = Number(
    section?.negativeMarks ?? 0
  );

  const totalMarks =
    selectionCount *
    marksPerQuestion;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">

        <div className="flex items-start gap-3">

          {/* Order */}

          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">
            {section?.order ??
              index + 1}
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900">
              {sectionName}
            </h3>

            <p className="mt-1 text-xs text-gray-500">
              {groupTypeLabel}
            </p>
          </div>

        </div>

        {/* Actions */}

        <div className="flex items-center gap-2">

          {onEdit && (
            <button
              type="button"
              onClick={() =>
                onEdit(
                  section,
                  index
                )
              }
              disabled={disabled}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Edit
            </button>
          )}

          {canRemove &&
            onRemove && (
              <button
                type="button"
                onClick={() =>
                  onRemove(
                    section,
                    index
                  )
                }
                disabled={disabled}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Remove
              </button>
            )}

        </div>
      </div>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <div className="p-5">

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">

          {/* Group Type */}

          <div>
            <p className="text-xs text-gray-500">
              Group Type
            </p>

            <p className="mt-1 text-sm font-semibold text-gray-800">
              {groupTypeLabel}
            </p>
          </div>

          {/* Chapter */}

          <div>
            <p className="text-xs text-gray-500">
              Chapter
            </p>

            <p className="mt-1 truncate text-sm font-semibold text-gray-800">
              {chapterName}
            </p>
          </div>

          {/* Selection Count */}

          <div>
            <p className="text-xs text-gray-500">
              Groups
            </p>

            <p className="mt-1 text-sm font-semibold text-gray-800">
              {selectionCount}
            </p>
          </div>

          {/* Marks */}

          <div>
            <p className="text-xs text-gray-500">
              Marks / Question
            </p>

            <p className="mt-1 text-sm font-semibold text-gray-800">
              {marksPerQuestion}
            </p>
          </div>

          {/* Negative */}

          <div>
            <p className="text-xs text-gray-500">
              Negative Marks
            </p>

            <p className="mt-1 text-sm font-semibold text-gray-800">
              {negativeMarks}
            </p>
          </div>

          {/* Total */}

          <div>
            <p className="text-xs text-gray-500">
              Section Marks
            </p>

            <p className="mt-1 text-sm font-bold text-indigo-700">
              {totalMarks.toFixed(2)}
            </p>
          </div>

        </div>

        {/* ===================================================
            BEHAVIOUR
        =================================================== */}

        <div className="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4">

          {section?.keepGroupTogether !==
            false && (
            <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
              Groups Together
            </span>
          )}

          {section?.randomizeGroups !==
            false && (
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
              Random Groups
            </span>
          )}

          {section
            ?.randomizeQuestionsWithinGroup ===
            true && (
            <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700">
              Random Questions
            </span>
          )}

          {negativeMarks > 0 && (
            <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700">
              Negative Marking
            </span>
          )}

        </div>
      </div>
    </div>
  );
}

/* =========================================================
   MAIN BLUEPRINT SECTION LIST
========================================================= */

export default function BlueprintSectionList({
  sections = [],
  onEdit,
  onRemove,
  onAddSection,
  disabled = false,
}) {
  /* =========================================================
     DEBUG
  ========================================================= */

  console.log(
    "##################################################"
  );

  console.log(
    "[BlueprintSectionList] RENDER"
  );

  console.log(
    "[BlueprintSectionList] sections:",
    sections
  );

  console.log(
    "[BlueprintSectionList] sections count:",
    Array.isArray(sections)
      ? sections.length
      : 0
  );

  console.log(
    "[BlueprintSectionList] onEdit:",
    onEdit
  );

  console.log(
    "[BlueprintSectionList] onRemove:",
    onRemove
  );

  console.log(
    "[BlueprintSectionList] onAddSection:",
    onAddSection
  );

  console.log(
    "##################################################"
  );

  /* =========================================================
     WATCH SECTIONS
  ========================================================= */

  useEffect(() => {
    console.log(
      "[BlueprintSectionList] sections changed:",
      sections
    );
  }, [sections]);

  /* =========================================================
     NORMALIZE
  ========================================================= */

  const normalizedSections =
    Array.isArray(sections)
      ? sections
      : [];

  /* =========================================================
     EMPTY
  ========================================================= */

  if (
    normalizedSections.length === 0
  ) {
    return (
      <EmptyState
        onAddSection={onAddSection}
        disabled={disabled}
      />
    );
  }

  /* =========================================================
     TOTALS
  ========================================================= */

  const totalConfiguredGroups =
    normalizedSections.reduce(
      (total, section) =>
        total +
        Number(
          section?.selectionCount ?? 0
        ),
      0
    );

  const totalConfiguredMarks =
    normalizedSections.reduce(
      (total, section) =>
        total +
        Number(
          section?.selectionCount ?? 0
        ) *
          Number(
            section?.marksPerQuestion ?? 0
          ),
      0
    );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="space-y-4">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Blueprint Sections
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            {normalizedSections.length}{" "}
            {normalizedSections.length === 1
              ? "section"
              : "sections"}{" "}
            configured
          </p>
        </div>

        {onAddSection && (
          <button
            type="button"
            onClick={onAddSection}
            disabled={disabled}
            className="w-fit rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            + Add Section
          </button>
        )}

      </div>

      {/* =====================================================
          SUMMARY
      ===================================================== */}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">

        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
          <p className="text-xs font-medium text-indigo-600">
            Sections
          </p>

          <p className="mt-1 text-xl font-bold text-indigo-800">
            {normalizedSections.length}
          </p>
        </div>

        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
          <p className="text-xs font-medium text-blue-600">
            Configured Groups
          </p>

          <p className="mt-1 text-xl font-bold text-blue-800">
            {totalConfiguredGroups}
          </p>
        </div>

        <div className="col-span-2 rounded-xl border border-green-100 bg-green-50 p-4 md:col-span-1">
          <p className="text-xs font-medium text-green-600">
            Configured Marks
          </p>

          <p className="mt-1 text-xl font-bold text-green-800">
            {totalConfiguredMarks.toFixed(
              2
            )}
          </p>
        </div>

      </div>

      {/* =====================================================
          SECTION CARDS
      ===================================================== */}

      <div className="space-y-3">

        {normalizedSections
          .slice()
          .sort(
            (a, b) =>
              Number(
                a?.order ?? 0
              ) -
              Number(
                b?.order ?? 0
              )
          )
          .map(
            (
              section,
              index
            ) => (
              <BlueprintSectionCard
                key={
                  section?._id ||
                  section?.id ||
                  `blueprint-section-${index}`
                }
                section={section}
                index={index}
                onEdit={onEdit}
                onRemove={onRemove}
                canRemove={
                  normalizedSections.length >
                  1
                }
                disabled={disabled}
              />
            )
          )}

      </div>

    </div>
  );
}

/* =========================================================
   PROP TYPES
========================================================= */

BlueprintSectionList.propTypes = {
  sections: PropTypes.array,

  onEdit: PropTypes.func,

  onRemove: PropTypes.func,

  onAddSection:
    PropTypes.func,

  disabled: PropTypes.bool,
};